import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack, ArrowForward, Close, Save } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton, { DarkGreyButton } from "../../../components/commonComponents/Buttons/ActionButton";
import WizardStepper from "../../../components/commonComponents/Progress/WizardStepper";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { StyledMainBox } from "../../../components/commonComponents/StyledComponents/styled";
import {
  checkDeviceStatus,
  fetchStoreLocations,
  fetchStoreSchedules,
  fetchTimezones,
  fetchCountriesList,
  legacyHoursToSchedule,
  saveStoreLocData,
  validateStoreGeneralInfo,
  validateStoreInfraInfo,
  validateStoreOpeningHours,
} from "../../../api_calls/infrastructure";
import { fetchStoreLevelCompanies, checkUserDetailsFromCookie } from "../../../api_calls/userManagement";
import { loadRegions } from "../../../api_calls/campaigns";
import { setBatchLoadingStatus } from "../../../api_calls/requestConfig";
import store from "../../../Store/store";
import { nextLocalScheduleId, WEEKDAY_KEYS } from "../../../api_calls/utils";
import StoreGeneralInfo from "./WizardSteps/StoreGeneralInfo";
import StoreOpeningHours from "./WizardSteps/StoreOpeningHours";
import StoreInfraInfo from "./WizardSteps/StoreInfraInfo";


const buildDefaultSchedule = () => ({
  id: nextLocalScheduleId(),
  name: "",
  validity_start: null,
  validity_end: null,
  no_end: false,
  is_collapsed: false,
  active: true,
  same_times_all_days: false,
  same_times_is_24h: false,
  same_times_ranges: [{ open: "08:00", close: "20:00" }],
  weekdays: WEEKDAY_KEYS.reduce((acc, day) => {
    acc[day] = { open: true, is_24h: false, time_ranges: [{ open: "08:00", close: "20:00" }] };
    return acc;
  }, {}),
});

// Collapse UI state must not mark the wizard dirty.
const fingerprintStoreLocData = (data) => {
  if (!data) return "{}";
  const { opening_schedules, ...rest } = data;
  return JSON.stringify({
    ...rest,
    opening_schedules: Array.isArray(opening_schedules)
      ? opening_schedules.map(({ is_collapsed, ...schedule }) => schedule)
      : opening_schedules,
  });
};

const AddStoreWizard = ({ open, closeModal, usedFor = "add", initialStep = 0 }) => {
  const userStateDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const loggedUserDetails = checkUserDetailsFromCookie();

  const [activeStep, setActiveStep] = React.useState(initialStep);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);
  // Snapshot of new_store_loc_data right after the prefill — used to detect real user edits
  const initialSnapshotRef = React.useRef(null);

  const stepLabels = ["General Information", "Opening Hours", "Infrastructure Information"];

  const storeName = userStateDetails?.new_store_loc_data?.store_name ?? "";

  const handleFieldChange = (storeLocData) => {
    const existing =
      typeof userStateDetails.new_store_loc_data !== "undefined" ? userStateDetails.new_store_loc_data : {};
    dispatchAction(updateStateData({ new_store_loc_data: { ...existing, ...storeLocData } }));
  };

  React.useEffect(() => {
    const overview = userStateDetails?.store_overview;

    const fetches = [
      fetchStoreLevelCompanies("stores"),
      fetchTimezones(),
      fetchCountriesList(),
      loadRegions(),
    ];

    // For the update flow, load existing schedules from the new API in the same batch
    if (usedFor === "update" && overview?.id) {
      fetches.push(fetchStoreSchedules(overview.id));
    }

    setBatchLoadingStatus(true);
    Promise.all(fetches)
      .then(() => {
        if (usedFor === "add") {
          handleFieldChange({
            opening_schedules: [buildDefaultSchedule()],
            automated_checkout: "Automated",
          });
          return;
        }

        if (usedFor === "update") {
          if (!overview) return;

          // Prefer schedules from the new API; fall back to converting legacy opening_hours
          const apiSchedules = store.getState().userSelections.store_regular_schedules;
          let opening_schedules;
          if (Array.isArray(apiSchedules) && apiSchedules.length > 0) {
            opening_schedules = apiSchedules.map((s) => ({
              ...s,
              id: nextLocalScheduleId(),
            }));
          } else {
            const legacy = legacyHoursToSchedule(overview.opening_hours);
            opening_schedules = legacy ? [legacy] : [buildDefaultSchedule()];
          }

          // Sort time ranges by open time on edit load (spec requirement)
          const sortByOpen = (arr, key = "open") =>
            [...arr].sort((a, b) => (a[key] > b[key] ? 1 : -1));
          opening_schedules = opening_schedules.map((s) => ({
            ...s,
            // Start collapsed so multi-schedule lists stay scannable.
            is_collapsed: true,
            same_times_ranges: sortByOpen(s.same_times_ranges || []),
            weekdays: Object.fromEntries(
              Object.entries(s.weekdays).map(([day, data]) => [
                day,
                { ...data, time_ranges: sortByOpen(data.time_ranges || []) },
              ])
            ),
          }));

          // Sort schedule boxes by start date once on load. While editing, cards
          // keep their order — live re-sorting on date changes is confusing.
          opening_schedules = [...opening_schedules].sort((a, b) => {
            if (!a.validity_start) return 1;
            if (!b.validity_start) return -1;
            return a.validity_start > b.validity_start ? 1 : -1;
          });

          handleFieldChange({
            id: overview.id,
            store_name: overview.name,
            store_country: overview?.country?.name,
            store_country_id: overview?.country?.id,
            store_region: overview?.region?.name,
            store_region_obj: overview?.region
              ? { label: overview.region.name, id: overview.region.id }
              : {},
            store_region_id: overview?.region?.id,
            store_street: overview.address,
            store_zip: overview.zipCode,
            store_city: overview.city,
            latitude: overview.latitude,
            longitude: overview.longitude,
            selected_timezone:
              overview?.timezone
                ? "(UTC " + overview.timezone.utc_offset + ") " + overview.timezone.name
                : "",
            selected_timezone_id: overview?.timezone?.id_timezone,
            share_list: overview?.company?.id,
            share_list_selection: overview?.company
              ? { label: overview.company.name, id: overview.company.id, tree_path: overview.company.tree_path }
              : {},
            retailer_store_id: overview.number_store_retailer,
            store_trolleys: overview.installed_trolleys,
            store_baskets: overview.installed_baskets,
            store_size: overview.store_size,
            store_status: checkDeviceStatus() ? "Active" : "Inactive",
            automated_checkout: overview.automated === true ? "Automated" : "Manual/No",
            opening_schedules,
          });
        }
      })
      .finally(() => {
        setBatchLoadingStatus(false);
        // Capture snapshot AFTER prefill so cancel detects only user-driven edits
        setTimeout(() => {
          initialSnapshotRef.current = fingerprintStoreLocData(
            store.getState().userSelections.new_store_loc_data
          );
        }, 0);
      });
  }, []);

  const validateCurrentStep = () => {
    const validators = {
      0: validateStoreGeneralInfo,
      1: validateStoreOpeningHours,
      2: validateStoreInfraInfo,
    };
    const result = validators[activeStep]();
    if (result.error === true) {
      setFieldErrors(result.errorMessages);
      return { error_found: true };
    }
    setFieldErrors({});
    return { error_found: false };
  };

  const handleSave = async () => {
    const schedules = userStateDetails?.new_store_loc_data?.opening_schedules ?? [];

    // Schedules are embedded directly in the store save payload via opening_hours_schedule
    const storeResult = await saveStoreLocData(usedFor, schedules);
    if (storeResult.success !== true) return;

    const storeId = usedFor === "add"
      ? storeResult.store_id
      : userStateDetails?.new_store_loc_data?.id;

    // Refresh Redux schedule state so the dashboard reflects the saved data
    if (storeId) await fetchStoreSchedules(storeId);

    fetchStoreLocations();
    closeModal();
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <StoreGeneralInfo fieldErrors={fieldErrors} />;
      case 1:
        return <StoreOpeningHours fieldErrors={fieldErrors} usedFor={usedFor} />;
      case 2:
        return <StoreInfraInfo fieldErrors={fieldErrors} usedFor={usedFor} />;
      default:
        return null;
    }
  };

  const hasDirtyData = (() => {
    if (initialSnapshotRef.current === null) return false;
    return fingerprintStoreLocData(userStateDetails.new_store_loc_data) !== initialSnapshotRef.current;
  })();

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => {
          e.preventDefault();
        }}
        fullWidth={false}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            width: "1320px",
            height: "850px",
            maxWidth: "none",
          },
        }}
      >
        <DialogTitle>
          {usedFor === "update" ? "Edit Store : " + storeName : "Add Store"}
        </DialogTitle>

        <DialogContent sx={{ overflowY: "auto" }}>
          <StyledMainBox component="main" sx={{ padding: 0 }}>
            <WizardStepper activeStep={activeStep} stepLabels={stepLabels} />
            <Box height={20} />
            {getStepContent(activeStep)}
          </StyledMainBox>
        </DialogContent>

        <Box height={20} />

        <DialogActions>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" width="100%">
            <CloseButton
              onClick={(e) => {
                e.preventDefault();
                if (hasDirtyData) {
                  setShowCancelConfirmation(true);
                } else {
                  closeModal();
                }
              }}
              label="Cancel"
              icon={<Close />}
            />

            <Stack direction="row" spacing={2} alignItems="flex-end">
              {activeStep >= 1 && (
                <DarkGreyButton
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveStep((prev) => prev - 1);
                  }}
                  label="Back"
                  icon={<ArrowBack />}
                />
              )}

              <ActionButton
                onClick={(e) => {
                  e.preventDefault();
                  const validation = validateCurrentStep();
                  if (validation.error_found === false) {
                    if (activeStep === 2) {
                      handleSave();
                    } else {
                      setActiveStep((prev) => prev + 1);
                    }
                  }
                }}
                label={activeStep === 2 ? "Save" : "Next"}
                icon={activeStep === 2 ? <Save /> : <ArrowForward />}
              />
            </Stack>
          </Stack>
        </DialogActions>

        {showCancelConfirmation && (
          <Confirmation
            showConfirmationPopup={showCancelConfirmation}
            closePopup={() => setShowCancelConfirmation(false)}
            confirmationActionCall={() => closeModal()}
            confirmationMessage={
              <Typography variant="body2">
                Are you certain you want to discard all the changes you have made?
              </Typography>
            }
            confirmationTitle={"Confirmation"}
          />
        )}
      </Dialog>
    </>
  );
};

export default AddStoreWizard;
